import React, { useMemo } from 'react';
import styled from 'styled-components';
import MultiSelectChips, { MultiSelectOption } from './MultiSelectChips';
import { useStaffList } from '../../hooks/useStaffList';
import { useAuth } from '../../contexts/AuthContext';
import type { Assignee } from '../../types/assignee';

interface StaffMultiPickerProps {
  /** מזהי העובדים המשויכים, לפי סדר השיוך */
  value: number[];
  onChange: (userIds: number[]) => void;
  /** משויכים ידועים עם שמות — כדי להציג משויך שאינו ברשימת הצוות (הושבת / הרשימה עוד נטענת) */
  knownAssignees?: Assignee[];
  /** תווית מעל הבורר. ריק ("") מסתיר את התווית (לשימוש inline). */
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const RequiredStar = styled.span`
  color: var(--error-color);
  margin-inline-end: 4px;
`;

const ErrorText = styled.span`
  color: var(--error-color);
  font-size: 13px;
`;

/**
 * בורר עובדים משויכים (רבים) לפריט מתוזמן — תגיות הניתנות להסרה + בורר להוספה.
 * אין מילוי-אוטומטי: ברירת המחדל (המשתמש הנוכחי) נקבעת ע"י הטופס ב-state ההתחלתי,
 * כך שאפשר להסיר גם את התגית האחרונה (והוולידציה תתריע).
 */
const StaffMultiPicker: React.FC<StaffMultiPickerProps> = ({
  value,
  onChange,
  knownAssignees,
  label = 'עובדים משויכים',
  required = false,
  error,
  disabled,
}) => {
  const { user } = useAuth();
  const staff = useStaffList();

  // צוות ∪ המשתמש הנוכחי ∪ המשויכים הידועים ∪ נבחרים שאין להם שם — אחרת MultiSelectChips מסתיר נבחר שאינו באפשרויות
  const options = useMemo<MultiSelectOption[]>(() => {
    const byId = new Map<number, string>();
    staff.forEach((s) => byId.set(s.id, s.fullName));
    if (user && !byId.has(user.id)) byId.set(user.id, user.fullName);
    knownAssignees?.forEach((a) => {
      if (!byId.has(a.userId)) byId.set(a.userId, a.fullName || `#${a.userId}`);
    });
    value.forEach((id) => {
      if (!byId.has(id)) byId.set(id, `#${id}`);
    });
    return Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
  }, [staff, user, knownAssignees, value]);

  return (
    <Wrapper>
      {label ? (
        <Label>
          {required && <RequiredStar>*</RequiredStar>}
          {label}
        </Label>
      ) : null}
      <MultiSelectChips
        options={options}
        selectedIds={value}
        onChange={onChange}
        disabled={disabled}
        placeholder="+ הוסף עובד"
        emptyText="לא נבחרו עובדים"
        invalid={!!error}
      />
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Wrapper>
  );
};

export default StaffMultiPicker;
